
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Product } from '@/components/product/ProductCard';

export function useProductData() {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Initial products fetch
  useEffect(() => {
    fetchProducts();
  }, []);

  // Filter products on search query change
  useEffect(() => {
    if (products.length) {
      const filtered = products.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (product.category && product.category.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (productsError) {
        throw productsError;
      }
      
      const { data: recommendationsData, error: recommendationsError } = await supabase
        .from('product_recommendations')
        .select(`
          *,
          packing_lists:packing_list_id (
            title
          )
        `)
        .order('created_at', { ascending: false });

      if (recommendationsError) {
        throw recommendationsError;
      }

      const formattedProducts = productsData.map(p => ({
        ...p,
        source: 'products' as const
      }));
      
      const formattedRecommendations = recommendationsData.map(r => ({
        ...r,
        source: 'recommendations' as const,
        packing_list_title: r.packing_lists?.title || 'Unknown Packing List'
      }));
      
      const allProducts = [...formattedProducts, ...formattedRecommendations];
      
      setProducts(allProducts);
      setFilteredProducts(allProducts);
      
      const uniqueCategories = [...new Set(
        allProducts
          .map(product => product.category)
          .filter(Boolean) as string[]
      )];
      
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Update local state after operations
  const updateLocalProductState = (updatedProducts: Product[]) => {
    setProducts(prev => {
      return prev.map(product => {
        const matchingProduct = updatedProducts.find(
          p => p.id === product.id && p.source === product.source
        );
        return matchingProduct || product;
      });
    });
    
    // Update filtered products as well
    setFilteredProducts(prev => {
      return prev.map(product => {
        const matchingProduct = updatedProducts.find(
          p => p.id === product.id && p.source === product.source
        );
        return matchingProduct || product;
      });
    });
  };

  // Remove products from local state
  const removeProductsFromState = (productsToRemove: Product[]) => {
    const productIds = productsToRemove.map(p => `${p.source}-${p.id}`);
    
    setProducts(prev => 
      prev.filter(p => !productIds.includes(`${p.source}-${p.id}`))
    );
    
    setFilteredProducts(prev => 
      prev.filter(p => !productIds.includes(`${p.source}-${p.id}`))
    );
  };

  return {
    products,
    filteredProducts,
    categories,
    loading,
    searchQuery,
    setSearchQuery,
    fetchProducts,
    updateLocalProductState,
    removeProductsFromState
  };
}
